import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import seon from '@seontechnologies/seon-javascript-sdk';
import { useMediaQueries } from 'seagull';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { LoginMethod, useSendLoginGtmEvents } from '~/hooks/useSendLoginGtmEvents';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest, sendErrReport } from '~/infra/utils';
import { errorMessages } from '~/pages/unAuthorizedPages/constants';
import { useStoreActions } from '~/store/store-context';
import styles from './GoogleButton.module.scss';
export default function GoogleButton(props) {
    const { ensureEmail, testId, onAuthSuccess, onAuthError } = props;
    const { reportToGtm, reportToAppcues, reportToHubspot, reportToHeap } = useEventReporter();
    const { api, logBI } = useStoreActions();
    const { mobile } = useMediaQueries();
    const getUtms = useUtmsParams();
    const { locationReload, GoogleButton, changeLocation, getOrigin } = useIoContext();
    const { pathname } = useLocation();
    const { sendGtmLoginSuccessEvent, sendGtmLoginFailEvent } = useSendLoginGtmEvents();
    const buttonConfiguration = useMemo(()=>{
        return {
            type: 'standard',
            locale: 'en_US',
            width: mobile ? 280 : 400,
            text: 'continue_with',
            theme: 'outline',
            logo_alignment: 'center',
            size: 'large'
        };
    }, [
        mobile
    ]);
    async function onGoogleAuth(param) {
        let { credential } = param;
        let clientContext;
        try {
            clientContext = await seon.getSession();
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'seon-oauth'
                }
            });
        }
        const authToken = await handleRequest(api.auth.googleSignIn({
            pageUrl: getOrigin() + pathname,
            idToken: credential,
            utms: getUtms(),
            ensureEmail,
            clientContext
        }));
        if (!authToken.success) {
            if (authToken.error.status === 403 || authToken.error.body === 'invalid auth request: email does not match') {
                sendGtmLoginFailEvent(LoginMethod.Google, 'email_does_not_match');
                changeLocation("".concat(pathname).concat(routes.loginError));
            }
            if (authToken.error.hasServerError(ServerError.EmailIsInvalid)) {
                onAuthError && onAuthError(errorMessages.noBusinessEmailError);
                sendGtmLoginFailEvent(LoginMethod.Google, 'invalid_email');
            } else {
                sendErrReport(authToken.error.body ? "GoogleButton - ".concat(authToken.error.body) : authToken.error);
                sendGtmLoginFailEvent(LoginMethod.Google);
                onAuthError && onAuthError();
            }
        } else if (authToken.data != null) {
            reportToGtm('form-success', 'google-sign-in');
            reportToAppcues('form-success', 'google-sign-in');
            reportToHubspot('google_sign_in');
            reportToHeap('googleSignIn', 'form-success', 'google-sign-in');
            logBI(11801);
            onAuthSuccess && onAuthSuccess();
            await sendGtmLoginSuccessEvent(LoginMethod.Google);
            locationReload();
        } else {
            sendErrReport('GoogleButton - did not retrieve an auth token');
            sendGtmLoginFailEvent(LoginMethod.Google);
            onAuthError && onAuthError();
        }
    }
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId || 'google-login',
        className: styles.buttonContainer,
        children: /*#__PURE__*/ _jsx(GoogleButton, {
            googleClientId: window.googleAuthClientId,
            callback: onGoogleAuth,
            buttonConfiguration: buttonConfiguration
        })
    });
}
